/**
 * Finding a person to put in a new group by typing their name (B1480).
 *
 * Mike, 2026-09-21, on the panel as it was: "when adding a name in a new message, you print out below
 * 'Nobody yet. Pick a colleague below, or type an address.' It should not work like this. you type and
 * then name comes up from the db just like in teams. How teams works is a new chat window opened and
 * you add into the chat by typing the name or pasting the external email of the person added."
 *
 * He is describing one box that does both, and the panel had two things that did neither: a wall of
 * forty name buttons to hunt through, and a separate address field beside it.
 *
 * The list being matched is his own colleagues from his own conversations (store.ts colleaguesToPick),
 * not a company directory -- a directory returns distribution lists, shared mailboxes and people who
 * have left, and picking the wrong row means adding somebody to a conversation.
 */

/** A person who can be put in a group: one of his colleagues, or an address he typed. */
export interface Pickable { name: string; address: string; lastSeen?: string | null }

/** How many matches a box like this may show before it stops being a box and becomes a list again. */
export const MOST_SHOWN = 8;

const fold = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/**
 * Does this person answer to what has been typed?
 *
 * EVERY WORD TYPED must appear somewhere in their name or address, in any order: "raff con" finds
 * Conor Rafferty and so does "con raff". Matching the whole string in order would fail both, and
 * matching ANY word would put every Conor in the company under a search for "conor rafferty".
 *
 * Accents are folded, because his colleagues include a Vincenzo and a Renaldo whose names are typed
 * both ways and neither spelling should hide the other.
 */
export function answersTo(person: Pickable, typed: string): boolean {
  const words = fold(typed).split(/\s+/).filter(Boolean);
  if (words.length === 0) return false;
  const hay = `${fold(person.name)} ${fold(person.address)}`;
  return words.every((w) => hay.includes(w));
}

/**
 * Who to offer for what has been typed: the people already picked are never offered again, the most
 * recently spoken to come first, and the list is cut at MOST_SHOWN.
 *
 * An EMPTY box offers his most recent colleagues rather than nothing. A box that offers nothing until
 * you type is a box you have to already know the answer to, and he has forty colleagues and remembers
 * the ones he spoke to this week.
 */
export function offer(colleagues: readonly Pickable[], typed: string, alreadyPicked: readonly string[]): Pickable[] {
  const taken = new Set(alreadyPicked.map((a) => a.trim().toLowerCase()));
  const said = typed.trim();
  return colleagues
    .filter((c) => !taken.has(c.address.trim().toLowerCase()))
    .filter((c) => said === '' || answersTo(c, said))
    .sort((a, b) => (b.lastSeen ?? '').localeCompare(a.lastSeen ?? ''))
    .slice(0, MOST_SHOWN);
}

/**
 * Is what has been typed an address in its own right?
 *
 * The same shape the card warning uses (cards.ts isAddressShaped), and deliberately shallow: this only
 * decides whether to OFFER to add it. Whether that person can actually be put in a Teams chat is
 * Microsoft's to answer, and it answers no for anybody who is not in the directory.
 */
export function looksLikeAddress(typed: string): boolean {
  const v = typed.trim().toLowerCase();
  const at = v.indexOf('@');
  if (at < 1 || at !== v.lastIndexOf('@') || at === v.length - 1) return false;
  if (/\s/.test(v)) return false;
  const domain = v.slice(at + 1);
  const dot = domain.indexOf('.');
  return dot > 0 && dot < domain.length - 1;
}

/** Where the highlight goes when the arrow keys move it: never off either end, and never onto nothing. */
export function moveHighlight(at: number, by: number, count: number): number {
  if (count <= 0) return -1;
  const next = at + by;
  if (next < 0) return count - 1;
  if (next >= count) return 0;
  return next;
}
